import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useCheckout } from "@/hooks/useCheckout";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import Header from "@/components/landing/Header";
import Hero from "@/components/landing/Hero";
import TrustSection from "@/components/landing/TrustSection";
import BenefitsSection from "@/components/landing/BenefitsSection";
import ResultsSection from "@/components/landing/ResultsSection";
import ReviewsSection from "@/components/landing/ReviewsSection";
import CheckoutSection from "@/components/landing/CheckoutSection";
import FaqSection from "@/components/landing/FaqSection";
import ContactSection from "@/components/landing/ContactSection";
import Footer from "@/components/landing/Footer";
import CheckoutModal from "@/components/landing/CheckoutModal";
import StickyOrderBar from "@/components/landing/StickyOrderBar";
import { useSeo, productJsonLd, faqJsonLd, SEO_KEYWORDS } from "@/hooks/useSeo";
import { useProductReviews } from "@/hooks/useProductReviews";
import { useTikTokViewContent } from "@/hooks/useTikTok";
import CustomerReviews from "@/components/landing/CustomerReviews";

export default function ProductDetailPage() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { setProduct: setCheckoutProduct, isOpen, closeCheckout } = useCheckout();

    useEffect(() => {
        setLoading(true);
        api.get(`/products/${slug}`)
            .then((r) => {
                setProduct(r.data);
                setCheckoutProduct(r.data);
            })
            .catch((e) =>
                setError(e?.response?.data?.detail || "Product not found"),
            )
            .finally(() => setLoading(false));
        return () => setCheckoutProduct(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const reviewSummary = useProductReviews(slug);

    useTikTokViewContent(product);

    useSeo({
        title: product ? `${product.name} Price in Pakistan | Resham Drops` : "",
        description: product
            ? `${product.short_description || product.tagline} Buy online in Pakistan from ${Math.min(...(product.bundles || []).map((b) => b.price))} PKR — cash on delivery nationwide.`
            : "",
        keywords: SEO_KEYWORDS,
        image: product?.images?.[0],
        path: `/product/${slug}`,
        jsonLd: product ? { "@context": "https://schema.org", "@graph": [productJsonLd(product, reviewSummary), faqJsonLd(product.faqs)].filter(Boolean) } : null,
    });

    if (loading) {
        return (
            <div className="min-h-screen bg-cream flex items-center justify-center text-forest-700 font-serif animate-pulse">
                Loading product…
            </div>
        );
    }

    if (error || !product) {
        return (
            <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-6 text-center">
                <h1 className="font-serif text-4xl font-bold text-forest-800">
                    Product not found
                </h1>
                <p className="text-forest-900/60 mt-3">{error}</p>
                <Link
                    to="/"
                    className="mt-6 inline-flex items-center gap-2 bg-forest-800 text-gold-400 px-6 py-3 rounded-full font-bold"
                >
                    <ChevronLeft className="w-4 h-4" /> Back to catalog
                </Link>
            </div>
        );
    }

    return (
        <div
            data-testid="product-detail-page"
            className="min-h-screen bg-cream text-forest-900 font-sans"
        >
            <AnnouncementBar />
            <Header />
            <main>
                <Hero product={product} />
                <TrustSection />
                <BenefitsSection product={product} />
                <ResultsSection />
                <ReviewsSection />
                <CustomerReviews product={product} summary={reviewSummary} />
                <CheckoutSection product={product} />
                <FaqSection product={product} />
                <ContactSection />
            </main>
            <Footer />
            <CheckoutModal
                product={product}
                open={isOpen}
                onClose={closeCheckout}
            />
            <StickyOrderBar product={product} />
        </div>
    );
}
